import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { debounce, isEmpty } from 'lodash';
import clsx from 'clsx';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import BobConfirmButton from './BobConfirmButton';
import SearchInput from '../containers/Global/SearchInput';

import * as BobActions from '../actions/bobActions';
import * as DashboardActions from '../actions/dashboardActions';

import classes from './SensorSelect.module.css';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const SiteScoreSelector = ({
    activeNavCompany,
    activeNavSite,
    clearEntitySensorData,
    dashboardEndTimeStamp,
    dashboardStartTimeStamp,
    desiredInterval,
    fetchScoringData,
    fetchSensorIds,
    ruleScoreKpis,
    selectedSensors,
    selectedEquipment,
    selectedEquipmentRule,
    selectedRuleSensors,
    selectedWeatherSensors,
    sensors,
    setSelectedRuleSensors,
    setSelectedSensors,
    setSelectedEquipmentRule,
    setSelectedWeatherSensors,
    setEntityScoringData,
    ...props
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [calculatedSensorSearchValue, setCalculatedSearchValue] = useState('');
    const [ruleSearchValue, setRuleSearchValue] = useState('');
    const [sensorMenuOpen, setSensorMenuOpen] = useState(false);
    const [ruleMenuOpen, setRuleMenuOpen] = useState(false);
    const [externalSensorMenuOpen, setExternalSensorMenuOpen] = useState(false);
    const [weatherSensorMenuOpen, setWeatherSensorMenuOpen] = useState(false);
    const [activeSensors, setActiveSensors] = useState([]);
    const [activeRule, setActiveRule] = useState({});
    const [activeWeatherSensors, setActiveWeatherSensors] = useState([]);
    const [rules, setRules] = useState([]);
    const [ruleSensors, setRuleSensors] = useState([]);
    const [calculatedSensorMenuOpen, setCalculatedSensorMenuOpen] = useState(false);
    const [calculatedSensors, setCalculatedSensors] = useState([]);
    const [activeExternalSensors, setActiveExternalSensors] = useState([]);
    const [externalSensors, setExternalSensors] = useState([]);
    const [externalSensorsList, setExternalSensorsList] = useState([]);
    const [hasCalculatedSensors, setHasCalulatedSensors] = useState(false);
    const [externalSearchValue, setExternalSearchValue] = useState('');

    useEffect(() => {
        setActiveSensors(selectedRuleSensors);
    }, [selectedRuleSensors]);

    useEffect(() => {
        let extSensors = [];
        activeSensors.forEach((item, index) => {
            if (!item.calculatedSensor) {
                if (!ruleSensors.find(sensor => sensor.id === item.id)) {
                    item.externalSensor = true;
                    extSensors.push(item);
                }
            }
        });
        setActiveExternalSensors(extSensors);
    }, [activeSensors]);

    useEffect(() => {
        const init = () => {
            setActiveRule(selectedEquipmentRule);
            let extSensors = [];

            selectedEquipmentRule.sensorIds.forEach(item => {
                if (!ruleSensors.find(sensor => sensor.id === item.id)) {
                    item.externalSensor = true;
                    extSensors.push(item);
                }
            });

            setExternalSensors(extSensors);
            setExternalSensorsList(extSensors);
            setActiveExternalSensors([]);
        };

        if (!isEmpty(selectedEquipmentRule)) init();
    }, [selectedEquipmentRule]);

    useEffect(() => {
        setRules(ruleScoreKpis);
    }, [ruleScoreKpis]);

    useEffect(() => {
        setRuleSensors(sensors.filter(s => s.calculatedSensor === false));
        const calculatedSensorList = sensors.filter(s => s.calculatedSensor === true);
        setCalculatedSensors(calculatedSensorList);
        setHasCalulatedSensors(!isEmpty(calculatedSensorList));
    }, [sensors]);

    useEffect(() => {
        setActiveWeatherSensors(selectedWeatherSensors);
    }, [selectedWeatherSensors]);

    const open = Boolean(anchorEl);
    const id = open ? 'nav-select-popover' : undefined;

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCheckbox = async (item, index) => {
        let updatedSensors = [...activeSensors];
        if (activeSensors.find(sensor => sensor.id === item.id)) {
            updatedSensors = updatedSensors.filter(sensor => sensor.id !== item.id);

            setActiveSensors(updatedSensors);
        } else {
            if (activeSensors.length >= 10) return;
            let newSensors = [];
            newSensors.push(item);
            updatedSensors = [...updatedSensors, ...newSensors];

            setActiveSensors(updatedSensors);
        }
    };

    const handleRuleCheckbox = async (item, index) => {
        if (activeRule.ruleId === item.ruleId) {
            setActiveRule({});
            setActiveSensors([]);
        } else {
            setActiveRule(item);
            const rule = [item.ruleId];
            setSelectedEquipmentRule(item);

            const ids = await fetchSensorIds(activeNavSite.id, selectedEquipment.id, rule);
            if (!isEmpty(ids)) {
                const udpatedSensors = ids.filter(s => s.id !== EMPTY_GUID);
                setActiveSensors(udpatedSensors);
            }
            else {
                setActiveSensors([]);
            }
        }
    };

    const handleExternalCheckbox = async item => {
        let updatedExternalSensors = [...activeExternalSensors];
        let updatedSensors = [...activeSensors];

        if (activeSensors.find(sensor => sensor.id === item.id)) {
            updatedExternalSensors = updatedExternalSensors.filter(sensor => sensor.id !== item.id);
            updatedSensors = updatedSensors.filter(sensor => sensor.id !== item.id);

            setActiveExternalSensors(updatedExternalSensors);
            setActiveSensors([...updatedSensors]);
        } else {
            let newSensors = [];
            newSensors.push(item);
            updatedExternalSensors = [...updatedExternalSensors, ...newSensors];

            setActiveExternalSensors(updatedExternalSensors);
            setActiveSensors([...updatedSensors, ...newSensors]);
        }
    };

    const handleWeatherCheckbox = async item => {
        let updatedWeatherSensors = [...activeWeatherSensors];

        if (activeWeatherSensors.find(sensor => sensor === item)) {
            updatedWeatherSensors = updatedWeatherSensors.filter(sensor => sensor !== item);

            setActiveWeatherSensors(updatedWeatherSensors);
        } else {
            let newSensors = [];
            newSensors.push(item);
            updatedWeatherSensors = [...updatedWeatherSensors, ...newSensors];
            setActiveWeatherSensors(updatedWeatherSensors);
        }
    };

    const isChecked = item => {
        if (activeSensors.find(sensor => sensor.id === item.id)) {
            return true;
        }
        return false;
    };

    const isRuleChecked = item => {
        if (item.ruleId === activeRule.ruleId) {
            return true;
        }
        return false;
    };

    const isWeatherChecked = item => {
        if (activeWeatherSensors.find(sensor => sensor === item)) {
            return true;
        }
        return false;
    };

    const handleGo = async () => {
        setSelectedRuleSensors(activeSensors);
        setSelectedWeatherSensors(activeWeatherSensors);

        let weatherSensors = '';
        if (!isEmpty(activeWeatherSensors)) {
            weatherSensors = activeNavSite.address.postalCode + ':' + activeWeatherSensors.join(':');
        }

        await fetchScoringData(activeNavCompany.id, activeSensors, [activeRule.ruleId], dashboardStartTimeStamp.format(), dashboardEndTimeStamp.format(), desiredInterval, { EquipmentId: selectedEquipment.id, weatherSensors });
        handleClose();
    };

    const handleClear = () => {
        setActiveRule({});
        setActiveSensors([]);
        setCalculatedSensors([]);
        setSelectedRuleSensors([]);
        setSelectedSensors([]);
        setSelectedEquipmentRule({});
        setEntityScoringData([]);
        clearEntitySensorData();
        setHasCalulatedSensors(false);
    };

    const handleExpandSensors = () => {
        setSensorMenuOpen(!sensorMenuOpen);
    };

    const handleExpandRules = () => {
        setRuleMenuOpen(!ruleMenuOpen);
    };

    const handleExpandCalculatedSensors = () => {
        setCalculatedSensorMenuOpen(!calculatedSensorMenuOpen);
    };

    const handleExpandExternalSensors = () => {
        setExternalSensorMenuOpen(!externalSensorMenuOpen);
    };

    const handleExpandWeatherSensors = () => {
        setWeatherSensorMenuOpen(!weatherSensorMenuOpen);
    };

    const handleSearch = searchFilter => {
        setSearchValue(searchFilter);
        const filteredSensors = sensors.filter(sensor => sensor.name.toLowerCase().includes(searchFilter.toLowerCase()));

        setRuleSensors(filteredSensors);
    };

    const handleCalculatedSensorFilter = searchFilter => {
        setCalculatedSearchValue(searchFilter);
        const filteredSensors = sensors.filter(sensor => sensor.calculatedSensor === true && sensor.name.toLowerCase().includes(searchFilter.toLowerCase()));

        setCalculatedSensors(filteredSensors);
    };

    const handleExternalSensorFilter = searchFilter => {
        setExternalSearchValue(searchFilter);
        const filteredSensors = externalSensors.filter(sensor => sensor.name.toLowerCase().includes(searchFilter.toLowerCase()));

        setExternalSensorsList(filteredSensors);
    };
    const handleRuleSearch = searchFilter => {
        setRuleSearchValue(searchFilter);

        const filteredRules = ruleScoreKpis.filter(rule => rule.ruleName.toLowerCase().includes(searchFilter.toLowerCase()));

        setRules(filteredRules);
    };

    const isSelected = item => {
        if (selectedSensors.find(sensor => sensor.id === item.id)) {
            return true;
        }
        return false;
    };

    const isExternalChecked = item => {
        if (activeExternalSensors.find(sensor => sensor === item)) {
            return true;
        }
        return false;
    };

    const sortedSensors = sensors => {
        let updatedSensors = [];
        updatedSensors = sensors.sort((a, b) => Number(isSelected(b)) - Number(isSelected(a)));
        return updatedSensors;
    };

    const formatLabel = () => {
        let label = 'Rules & Sensors - ';
        label = isEmpty(activeRule) ? label + '0 Rules, ' : label + '1 Rule, ';
        label = activeSensors.length === 1 ? label + `${activeSensors.length} Sensor ` : label + `${activeSensors.length} Sensors `;

        return label;
    };

    return (
        <div className={classes.SensorSelect}>
            <Button
                aria-describedby={id}
                onClick={handleClick}
                disabled={!sensors.length}
            >
                <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    title="sensor"
                    placement="bottom"
                >
                    <div className={classes.Selector}>
                        <p>{formatLabel()}</p>
                    </div>
                </Tooltip>
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical:   'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical:   'top',
                    horizontal: 'left',
                }}
            >
                <List className={classes.SiteMenuItem}>
                    <ListItem
                        key={0}
                        onClick={() => handleExpandRules()}
                        classes={{
                            root: classes.RuleListItem
                        }}
                    >
                        <i className={clsx(`fas fa-chevron-${ruleMenuOpen ? 'down' : 'up'}`, classes.ArrowIcon)} aria-hidden="true" />
                        {
                            activeRule.ruleName ?
                                `Rules - ${activeRule.ruleName}`
                                :
                                'Rules'
                        }
                    </ListItem>
                    {
                        ruleMenuOpen ?
                            <div className={classes.List}>
                                <SearchInput
                                    className={classes.SearchInput}
                                    placeholder={'Search'}
                                    search={debounce(handleRuleSearch, 500)}
                                    value={ruleSearchValue}
                                    noRadius
                                />
                                <List className={classes.RuleList}>
                                    {rules.map((item, index) => (
                                        <ListItem
                                            key={index}
                                            onClick={() => handleRuleCheckbox(item, index)}
                                            classes={{
                                                root: classes.ListItem
                                            }}
                                        >
                                            <Checkbox
                                                style={{
                                                    color: 'black',
                                                }}
                                                checked={isRuleChecked(item)}
                                            />
                                            {item.ruleName}
                                        </ListItem>
                                    ))}
                                </List>
                            </div>
                            :
                            null
                    }
                </List>
                <List className={classes.SiteMenuItem}>
                    <ListItem
                        key={0}
                        onClick={() => handleExpandSensors()}
                        classes={{
                            root: classes.RuleListItem
                        }}
                    >
                        <i className={clsx(`fas fa-chevron-${sensorMenuOpen ? 'down' : 'up'}`, classes.ArrowIcon)} aria-hidden="true" />
                        {
                            activeSensors.length ?
                                `Sensors - ${activeSensors.filter(item => !item.calculatedSensor && !item.externalSensor).length} Selected`
                                :
                                'Sensors'
                        }
                    </ListItem>
                    {
                        sensorMenuOpen ?
                            <div className={classes.List}>
                                <SearchInput
                                    className={classes.SearchInput}
                                    placeholder={'Search'}
                                    search={debounce(handleSearch, 500)}
                                    value={searchValue}
                                    noRadius
                                />
                                <List className={classes.RuleList}>
                                    {sortedSensors(ruleSensors).map((item, index) => (
                                        <ListItem
                                            key={index}
                                            onClick={() => handleCheckbox(item, index)}
                                            classes={{
                                                root: classes.ListItem
                                            }}
                                        >
                                            <Checkbox
                                                style={{
                                                    color: 'black',
                                                }}
                                                checked={isChecked(item)}
                                            />
                                            {item.name}
                                        </ListItem>
                                    ))}
                                </List>
                            </div>
                            :
                            null
                    }
                </List>
                { hasCalculatedSensors ?
                    <List className={classes.SiteMenuItem}>
                        <ListItem
                            key={0}
                            onClick={() => handleExpandCalculatedSensors()}
                            classes={{
                                root: classes.RuleListItem
                            }}
                        >
                            <i className={clsx(`fas fa-chevron-${calculatedSensorMenuOpen ? 'down' : 'up'}`, classes.ArrowIcon)} aria-hidden="true" />
                            {
                                activeSensors.filter(s => s.calculatedSensor).length ?
                                    `Calculated Sensors - ${activeSensors.filter(s => s.calculatedSensor).length} Selected`
                                    :
                                    'Calculated Sensors'
                            }
                        </ListItem>
                        {
                            calculatedSensorMenuOpen ?
                                <div className={classes.List}>
                                    <SearchInput
                                        className={classes.SearchInput}
                                        placeholder={'Search'}
                                        search={debounce(handleCalculatedSensorFilter, 500)}
                                        value={calculatedSensorSearchValue}
                                        noRadius
                                    />
                                    <List className={classes.RuleList}>
                                        {sortedSensors(calculatedSensors).map((item, index) => (
                                            <ListItem
                                                key={index}
                                                onClick={() => handleCheckbox(item, index)}
                                                classes={{
                                                    root: classes.ListItem
                                                }}
                                            >
                                                <Checkbox
                                                    style={{
                                                        color: 'black',
                                                    }}
                                                    checked={isChecked(item)}
                                                />
                                                {item.name}
                                            </ListItem>
                                        ))}
                                    </List>
                                </div>
                                :
                                null
                        }
                    </List>
                    : null 
                }
                {!isEmpty(externalSensors) ?
                    <List className={classes.SiteMenuItem}>
                        <ListItem
                            key={0}
                            onClick={() => handleExpandExternalSensors()}
                            classes={{
                                root: classes.RuleListItem
                            }}
                        >
                            <i className={clsx(`fas fa-chevron-${externalSensorMenuOpen ? 'down' : 'up'}`, classes.ArrowIcon)} aria-hidden="true" />
                            {
                                activeExternalSensors.length ?
                                    `External Sensors - ${activeExternalSensors.length} Selected`
                                    :
                                    'External Sensors'
                            }
                        </ListItem>
                        {
                            externalSensorMenuOpen ?
                                <div className={classes.List}>
                                    <SearchInput
                                        className={classes.SearchInput}
                                        placeholder={'Search'}
                                        search={debounce(handleExternalSensorFilter, 500)}
                                        value={externalSearchValue}
                                        noRadius
                                    />
                                    <List className={classes.RuleList}>
                                        {sortedSensors(externalSensorsList).map((item, index) => (
                                            <ListItem
                                                key={index}
                                                onClick={() => handleExternalCheckbox(item)}
                                                classes={{
                                                    root: classes.ListItem
                                                }}
                                            >
                                                <Checkbox
                                                    style={{
                                                        color: 'black',
                                                    }}
                                                    checked={isExternalChecked(item)}
                                                />
                                                {item.name}
                                            </ListItem>
                                        ))}
                                    </List>
                                </div>
                                :
                                null
                        }
                    </List>
                    : null
                }
                <List className={classes.SiteMenuItem}>
                    <ListItem
                        key={0}
                        onClick={() => handleExpandWeatherSensors()}
                        classes={{
                            root: classes.RuleListItem
                        }}
                    >
                        <i className={clsx(`fas fa-chevron-${weatherSensorMenuOpen ? 'down' : 'up'}`, classes.icon)} aria-hidden="true" />
                        {
                            activeWeatherSensors.length ?
                                `Weather Sensors - ${activeWeatherSensors.length} Selected`
                                :
                                'Weather Sensors'
                        }
                    </ListItem>
                    {
                        weatherSensorMenuOpen ?
                            <div className={classes.List}>
                                <List className={classes.RuleList}>
                                    <ListItem
                                        key={0}
                                        onClick={() => handleWeatherCheckbox('temperature')}
                                        classes={{
                                            root: classes.ListItem
                                        }}
                                    >
                                        <Checkbox
                                            style={{
                                                color: 'black',
                                            }}
                                            checked={isWeatherChecked('temperature')}
                                        />
                                        Temperature
                                    </ListItem>
                                    <ListItem
                                        key={1}
                                        onClick={() => handleWeatherCheckbox('humidity')}
                                        classes={{
                                            root: classes.ListItem
                                        }}
                                    >
                                        <Checkbox
                                            style={{
                                                color: 'black',
                                            }}
                                            checked={isWeatherChecked('humidity')}
                                        />
                                        Humidity
                                    </ListItem>
                                    <ListItem
                                        key={2}
                                        onClick={() => handleWeatherCheckbox('pressure')}
                                        classes={{
                                            root: classes.ListItem
                                        }}
                                    >
                                        <Checkbox
                                            style={{
                                                color: 'black',
                                            }}
                                            checked={isWeatherChecked('pressure')}
                                        />
                                        Pressure
                                    </ListItem>
                                    <ListItem
                                        key={3}
                                        onClick={() => handleWeatherCheckbox('minTemperature')}
                                        classes={{
                                            root: classes.ListItem
                                        }}
                                    >
                                        <Checkbox
                                            style={{
                                                color: 'black',
                                            }}
                                            checked={isWeatherChecked('minTemperature')}
                                        />
                                        Min Temperature
                                    </ListItem>
                                    <ListItem
                                        key={4}
                                        onClick={() => handleWeatherCheckbox('maxTemperature')}
                                        classes={{
                                            root: classes.ListItem
                                        }}
                                    >
                                        <Checkbox
                                            style={{
                                                color: 'black',
                                            }}
                                            checked={isWeatherChecked('maxTemperature')}
                                        />
                                        Max Temperature
                                    </ListItem>
                                </List>
                            </div>
                            :
                            null
                    }
                </List>

                <div className={isEmpty(activeRule) ? classes.HelperTextError : classes.HelperText}>
                    <i className={clsx('fas fa-info-circle', classes.HelpIcon)} />
                    You must select a rule
                </div>
                <div className={activeSensors.length >= 10 ? classes.HelperTextError : classes.HelperText}>
                    <i className={clsx('fas fa-info-circle', classes.HelpIcon)} />
                    You can only select up to 10 sensors
                </div>
                <div className={classes.ScoreButtonContainer}>
                    <BobConfirmButton
                        className={isEmpty(activeRule) ? classes.DisabledGoButton : null}
                        disabled={isEmpty(activeRule)}
                        label={'Go'}
                        onClick={handleGo}
                    />
                    <BobConfirmButton
                        className={classes.Buttons}
                        label={'Clear'}
                        onClick={handleClear}
                    />
                </div>
            </Popover>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany:        state.bob.activeNavCompany,
        activeNavSite:           state.bob.activeNavSite,
        dashboardEndTimeStamp:   state.dashboard.dashboardEndTimeStamp,
        dashboardStartTimeStamp: state.dashboard.dashboardStartTimeStamp,
        desiredInterval:         state.dashboard.desiredInterval,
        selectedRule:            state.dashboard.selectedRule,
        selectedRuleSensors:     state.dashboard.selectedRuleSensors,
        selectedSensors:         state.dashboard.selectedSensors,
        selectedWeatherSensors:  state.dashboard.selectedWeatherSensors,
        activeNavEquipment:      state.bob.activeNavEquipment,
        ruleScoreKpis:           state.dashboard.ruleScoreKpis,
        ruleScoringConfig:       state.dashboard.ruleScoringConfig
    };
};

const mapDispatchToProps = {
    ...BobActions,
    ...DashboardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(SiteScoreSelector);
